import { RefObject, useEffect } from 'react';

const prefetchedSpriteUrls = new Set<string>();

const prefetchImage = (url: string) => {
  if (!url || prefetchedSpriteUrls.has(url)) {
    return;
  }

  prefetchedSpriteUrls.add(url);
  const image = new Image();
  image.src = url;
};

export function useSpriteSheetPrefetch(
  containerRef: RefObject<HTMLElement>,
  urls: Array<string | undefined>,
  enabled = true
) {
  useEffect(() => {
    if (!enabled || !containerRef.current) {
      return;
    }

    const validUrls = urls.filter((url): url is string => Boolean(url));
    if (!validUrls.length) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      validUrls.forEach(prefetchImage);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          validUrls.forEach(prefetchImage);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [containerRef, enabled, urls]);
}

export const markSpriteSheetLoaded = (url: string) => {
  if (url) {
    prefetchedSpriteUrls.add(url);
  }
};

export const isSpriteSheetPrefetched = (url?: string) => !!url && prefetchedSpriteUrls.has(url);
